<template>
    <v-dialog
        v-model="dialog"
        fullscreen
        hide-overlay
        transition="dialog-bottom-transition"
    >
        <v-card
            :loading="isLoading"
        >
            <v-toolbar
                dark
                color="primary"
            >
                <v-btn
                    icon
                    dark
                    @click="dialog = false"
                    :disabled="isSaving"
                >
                    <v-icon>
                        mdi-close
                    </v-icon>
                </v-btn>
                <v-toolbar-title>
                    {{ $t('rula.title', {id: booking.booking_no}) }}
                </v-toolbar-title>
                <v-toolbar-items v-if="booking._meta.rulaReleased">
                    <v-chip color="warning">{{ $t('rula.released') }}</v-chip>
                </v-toolbar-items>
            </v-toolbar>
            <v-card-text>
                <v-container
                    v-if="!isLoading"
                    class="ma-0"
                    fluid
                >
                    <v-card :loading="commentSaving" class="mb-16">
                        <v-card-text>
                            <v-textarea :label="$t('rula.comments')"
                                        @input="handleInput"
                                        :value="booking._meta.comment" ref="rulaComment" />
                        </v-card-text>
                        <v-card-actions>
                            <v-btn :disabled="!commentDirty" @click.prevent="saveComment">Submit</v-btn>
                        </v-card-actions>
                    </v-card>
                    <rula-category
                        v-for="category in categories"
                        :key="category.category"
                        :can-edit="canEdit"
                        :category="category"
                        :categories="categories"
                        :booking="booking"
                        class="mb-16"
                        @updateOnSite="updateOnSite"
                        @change="updateData"
                        @updateNotAvailable="updateNotAvailable"
                        @deleteAsset="deleteAsset"
                        :loading="categorySaving[category.category]"
                    />
                </v-container>
                <v-container
                    v-else
                >
                    <v-skeleton-loader
                        type="card"
                    >
                    </v-skeleton-loader>
                </v-container>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>

import RulaCategory from "@/components/Booking/RulaCategory";
import axios from "axios";
import { mapActions } from "vuex";
import { SnackBarAction, SnackBarTypes } from "@/store/snackbar/types";
import Vue from "vue";
import {getRulaCategories} from "@/utils/rula";

export default {
    components: {
        RulaCategory,
    },
    props: {
        value: {
            type: Boolean,
            required: true,
            default: false,
        },
        booking: {
            type: Object,
            required: true,
        },
    },
    data: () => ({
        isLoading: false,
        isSaving: false,
        data: [],
        commentDirty: false,
        comment: null,
        commentSaving: false,
        categorySaving: {
            hotel: false,
            gpx: false,
            route: false,
            maps: false,
            baggageTag: false,
            voucher: false,
            arrival: false,
            otherDocuments: false,
            otherPartnerInfo: false,
        },
    }),
    watch: {
        dialog: function (newValue) {
            if(newValue) {
                this.isLoading = true;

                this.data = [];

                axios
                    .get(`/booking/${this.booking.booking_no}/rula`)
                    .then(resp => {
                        return resp.data;
                    })
                    .then(data => {
                        this.data = JSON.parse(JSON.stringify(data));
                    })
                    .catch(err => {
                        if(err.response?.status !== 404) {
                            this.setSnackbar(
                                this.$t('errors.loading'),
                                SnackBarTypes.ERROR
                            );
                            console.error(err);
                        }
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            }
        },
    },
    computed: {
        canEdit() {
            // always allow editing, changes will result in message to operations
            return true;
            //return !this.booking._meta.rulaReleased || this.$can('admin');
        },
        categories() {
            let categories = [];

            let rulaCategories = getRulaCategories();

            for(let i = 0, li = rulaCategories.length; i < li; i++) {
                let tmp = null;
                for(let j = 0, lj = this.data.length; j < lj; j++) {
                    if(this.data[j].category === rulaCategories[i]) {
                        tmp = this.data[j];
                        break;
                    }
                }

                if(tmp !== null) {
                    categories.push(tmp);
                } else {
                    categories.push({
                        category: rulaCategories[i],
                        files: [],
                        onSite: false,
                    });
                }
            }

            return categories;
        },
        dialog: {
            get: function() {
                return this.value;
            },
            set: function(newValue) {
                this.$emit('input', newValue);
            }
        },
    },
    methods: {
        ...mapActions({
            setSnackbar: `snackbar/${SnackBarAction.SET}`,
        }),
        updateOnSite(
            payload
        ) {
            this.categorySaving[payload.category.category] = true;

            axios
                .post(
                    `/booking/${this.booking.booking_no}/rula/${payload.category.category}/on-site`,
                    {
                        onSite: payload.onSite,
                    }
                )
                .then(resp => {
                    return resp.data;
                })
                .then(data => {
                    this.updateData(data);
                })
                .catch((err) => {
                    this.setSnackbar(
                        this.$t('errors.save'),
                        SnackBarTypes.ERROR
                    );
                    console.error(err);
                })
                .finally(() => {
                    this.categorySaving[payload.category.category] = false;
                })
        },
        updateNotAvailable(
            payload
        ) {
            this.categorySaving[payload.category.category] = true;

            axios
                .post(
                    `/booking/${this.booking.booking_no}/rula/${payload.category.category}/not-available`,
                    {
                        notAvailable: payload.notAvailable,
                    }
                )
                .then(resp => {
                    return resp.data;
                })
                .then(data => {
                    this.updateData(data);
                })
                .catch((err) => {
                    this.setSnackbar(
                        this.$t('errors.save'),
                        SnackBarTypes.ERROR
                    );
                    console.error(err);
                })
                .finally(() => {
                    this.categorySaving[payload.category.category] = false;
                })
        },
        deleteAsset(
            payload
        ) {
            this.isSaving = true;

            axios
                .delete(
                    `/booking/${this.booking.booking_no}/rula/${payload.id}`,
                )
                .then(resp => {
                    return resp.data;
                })
                .then(() => {
                    for(let i = 0, l = this.data.length; i < l; i++) {
                        if(this.data[i].category === payload.category.category) {
                            for(let j = 0, jl = this.data[i].files.length; j < jl; j++) {
                                if(this.data[i].files[j].id === payload.id) {
                                    this.data[i].files.splice(j, 1);
                                    return;
                                }
                            }
                        }
                    }
                })
                .catch((err) => {
                    this.setSnackbar(
                        this.$t('errors.delete'),
                        SnackBarTypes.ERROR
                    );
                    console.error(err);
                })
                .finally(() => {
                    this.isSaving = false;
                })
        },
        updateData(data) {
            let found = false;
            for(let i = 0, l = this.data.length; i < l; i++) {
                if(this.data[i].category === data.category) {
                    Vue.set(this.data, i, data);
                    found = true;
                    break;
                }
            }
            if(!found) {
                this.data.push(data);
            }
        },
        getCategory(category) {
            for(let i = 0, l = this.categories.length; i < l; i++) {
                if(this.categories[i].category === category) {
                    return this.categories[i];
                }
            }

            return null;
        },
        saveComment(){
            this.commentSaving = true
            axios
                .post(`/booking/${this.booking.booking_no}/comment`,{
                    comment: this.comment
                })
            .then(res => res.data)
            .then(data => {
               this.commentSaving = false;
               this.commentDirty = false;
                this.$emit('comment', data.comment);
            })

        },
        handleInput(arg){
            this.comment = arg;
            this.commentDirty = true;
        }
    },
}
</script>
